import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useToast } from '../services/ToastContext';
import '../styles/CommentSection.css';

function Comment({ comment, allComments, currentUserId, onDelete, onReplyAdded, depth }) {
  const [replying, setReplying] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const replies = allComments.filter(c => c.parent_id === comment.id);

  const formatTime = (ds) => {
    const diff = (Date.now() - new Date(ds)) / 1000 / 60;
    if (diff < 1) return 'just now';
    if (diff < 60) return `${Math.floor(diff)}m`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h`;
    return new Date(ds).toLocaleDateString();
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim()) return;
    setSubmitting(true);
    try {
      const res = await api.post('/comments/', {
        post_id: comment.post_id,
        content: replyText.trim(),
        parent_id: comment.id
      });
      onReplyAdded(res.data.comment);
      setReplyText('');
      setReplying(false);
    } catch {
      // silently fail — parent can handle errors if needed
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`comment${depth > 0 ? ' comment-reply' : ''}`}>
      <div className="comment-avatar">
        {comment.profiles?.avatar_url
          ? <img src={comment.profiles.avatar_url} alt="" />
          : comment.profiles?.full_name?.charAt(0).toUpperCase()
        }
      </div>
      <div className="comment-body">
        <div className="comment-header">
          <span className="comment-author">{comment.profiles?.full_name}</span>
          <span className="comment-time">{formatTime(comment.created_at)}</span>
          {comment.author_id === currentUserId && (
            <button className="comment-delete-btn" onClick={() => onDelete(comment.id)} title="Delete">×</button>
          )}
        </div>
        <p className="comment-text">{comment.content}</p>
        {depth === 0 && (
          <button className="comment-reply-btn" onClick={() => setReplying(r => !r)}>
            {replies.length > 0 ? `Reply · ${replies.length}` : 'Reply'}
          </button>
        )}
        {replying && (
          <form onSubmit={handleReply} className="reply-form">
            <input
              value={replyText}
              onChange={e => setReplyText(e.target.value)}
              placeholder="Write a reply…"
              disabled={submitting}
              autoFocus
            />
            <button type="submit" disabled={submitting || !replyText.trim()}>Post</button>
          </form>
        )}
        {replies.map(reply => (
          <Comment
            key={reply.id}
            comment={reply}
            allComments={allComments}
            currentUserId={currentUserId}
            onDelete={onDelete}
            onReplyAdded={onReplyAdded}
            depth={depth + 1}
          />
        ))}
      </div>
    </div>
  );
}

function CommentSection({ postId, currentUserId }) {
  const { addToast } = useToast();
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const topLevel = comments.filter(c => !c.parent_id);

  const loadComments = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/comments/?post_id=${postId}`);
      setComments(res.data.comments);
      setLoaded(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (expanded && !loaded) {
      loadComments();
    }
  }, [expanded]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    setSubmitting(true);
    try {
      const res = await api.post('/comments/', {
        post_id: postId,
        content: newComment.trim()
      });
      setComments(prev => [...prev, res.data.comment]);
      setNewComment('');
    } catch {
      addToast('Failed to post comment', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (commentId) => {
    try {
      await api.delete(`/comments/${commentId}`);
      setComments(prev => prev.filter(c => c.id !== commentId && c.parent_id !== commentId));
    } catch {}
  };

  const handleReplyAdded = (comment) => {
    setComments(prev => [...prev, comment]);
  };

  const count = comments.length;

  return (
    <div className="comment-section">
      <button className="comment-toggle-btn" onClick={() => setExpanded(e => !e)}>
        {expanded ? 'Hide comments' : count > 0 ? `${count} comment${count !== 1 ? 's' : ''}` : 'Comment'}
      </button>

      {expanded && (
        <div className="comments-list">
          {loading ? (
            <p className="comments-loading">Loading…</p>
          ) : (
            topLevel.map(comment => (
              <Comment
                key={comment.id}
                comment={comment}
                allComments={comments}
                currentUserId={currentUserId}
                onDelete={handleDelete}
                onReplyAdded={handleReplyAdded}
                depth={0}
              />
            ))
          )}
          <form onSubmit={handleSubmit} className="new-comment-form">
            <input
              value={newComment}
              onChange={e => setNewComment(e.target.value)}
              placeholder="Write a comment…"
              disabled={submitting}
            />
            <button type="submit" disabled={submitting || !newComment.trim()}>Post</button>
          </form>
        </div>
      )}
    </div>
  );
}

export default CommentSection;
